import { Form, Image, InputNumber, Space, Table } from "antd";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";
import style from '../../index.module.scss'
import { createContext, useContext, useEffect, useRef, useState } from "react";

const cx = classNames.bind(style)
const EditableContext = createContext(null);
export default function TableSushiBox({
    loading,
    data = [],
    editMode,
    handleOnCell = () => undefined,
    shopInfo,
    type,
    renderQuantity,
    onlyVegetarian,
}) {
    const { t } = useTranslation()

    const EditableRow = ({ index, ...props }) => {
        const [form] = Form.useForm();
        return (
            <Form form={form} component={false}>
                <EditableContext.Provider value={form}>
                    <tr {...props} />
                </EditableContext.Provider>
            </Form>
        );
    };

    const EditableCell = ({
        title,
        editable,
        children,
        dataIndex,
        record,
        handleSave,
        ...restProps
    }) => {
        const [editing, setEditing] = useState(false);
        const inputRef = useRef(null);
        const form = useContext(EditableContext);
        useEffect(() => {
            if (editing) {
                inputRef.current.focus();
            }
        }, [editing]);
        const toggleEdit = () => {
            if (!editMode) return
            setEditing(!editing);
            form.setFieldsValue({
                [dataIndex]: record[dataIndex],
            });
        };
        const save = async (dataIndex) => {
            try {
                const values = await form.validateFields();
                toggleEdit();
                handleSave({
                    ...record,
                    ...values,
                });
            } catch (errInfo) {
                console.log('Save failed:', errInfo);
            }
        };

        const renderItem = () => {
            return <InputNumber ref={inputRef} min={0} onPressEnter={() => save(dataIndex)} onBlur={() => save(dataIndex)} />
        }
        let childNode = children;
        if (editable) {
            childNode = editing ? (
                <Form.Item
                    style={{
                        margin: 0,
                    }}
                    name={dataIndex}
                >
                    {renderItem()}
                </Form.Item>
            ) : (
                <div
                    className={cx({ 'editable-cell-value-wrap': editMode })}
                    onClick={toggleEdit}
                >
                    {children}
                </div>
            );
        }
        return <td {...restProps}>{childNode}</td>;
    };

    const columns = [
        {
            title: t('name'),
            dataIndex: "box",
            key: "box",
            render: (_) => (
                <Space direction="vertical">
                    <Image width={60} src={_?.imageUrl} preview={false} />
                    <b style={{ fontSize: '16px' }}>{_.name}</b>
                </Space>
            ),
            onCell: (record) => handleOnCell(record, t('name'), 'box')
        },
    ]

    if (renderQuantity.includes('shop')) {
        columns.push({
            title: t('shop'),
            dataIndex: "quantity",
            key: "quantity",
            render: (_) => {
                return (
                    <b style={{ fontSize: '16px' }}>{_}</b>
                );
            },
            onCell: (record) => handleOnCell(record, t('shop'), 'quantity', true)
        })
    }

    if (renderQuantity.includes('cabinet1') && shopInfo?.cabinet > 0) {
        columns.push({
            title: shopInfo?.cabinet_code_1 ? shopInfo.cabinet_code_1 : t('cabinet', { value: 1 }),
            dataIndex: "quantityCabinet1",
            key: "quantityCabinet1",
            render: (_) => {
                return (
                    <b style={{ fontSize: '16px' }}>{_}</b>
                );
            },
            onCell: (record) => handleOnCell(record, t('cabinet', { value: 1 }), 'quantityCabinet1', true)
        })
    }

    if (renderQuantity.includes('cabinet2') && shopInfo?.cabinet > 1) {
        columns.push({
            title: shopInfo?.cabinet_code_2 ? shopInfo.cabinet_code_2 : t('cabinet', { value: 2 }),
            dataIndex: "quantityCabinet2",
            key: "quantityCabinet2",
            render: (_) => {
                return (
                    <b style={{ fontSize: '16px' }}>{_}</b>
                );
            },
            onCell: (record) => handleOnCell(record, t('cabinet', { value: 2 }), 'quantityCabinet2', true)
        })
    }

    if (renderQuantity.includes('boxOrder')) {
        columns.push({
            title: t('boxIndividualOrder'),
            dataIndex: "quantityOrder",
            key: "quantityOrder",

            render: (_) => {
                return (
                    <b style={{ fontSize: '16px' }}>{_}</b>
                );
            },
            onCell: (record) => handleOnCell(record, t('boxIndividualOrder'), 'quantityOrder')
        })
    }

    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    };
    
    const filterData = (item) => {
        if (item.box.type !== type) return false;
        if (onlyVegetarian && !item.box.isVegetarian) return false;

        if (!editMode) {
            const conditions = [];

            if (renderQuantity.includes('shop')) {
                conditions.push(item.quantity > 0);
            }
            if (renderQuantity.includes('cabinet1')) {
                conditions.push(item.quantityCabinet1 > 0);
            }
            if (renderQuantity.includes('cabinet2')) {
                conditions.push(item.quantityCabinet2 > 0);
            }
            if (renderQuantity.includes('boxOrder')) {
                conditions.push(item.quantityOrder > 0);
            }

            if (conditions.length > 0 && !conditions.some(Boolean)) {
                return false;
            }
        }

        return true;
    };

    return (
        <Table
            components={components}
            loading={loading}
            columns={columns}
            dataSource={data.filter(filterData)}
            size="small"
            pagination={false}
            bordered />
    );
}
